import { getEditorialCapabilities, getSettledEditorialCapabilities } from 'app/models/editorialCapabilities';
import { USER_ROLES } from 'app/models/user/user.constants';

let session = null;
let scope = null;
let revision = 0;
// Revisión de la última invalidación. La revisión sube en cada publicación y en cada pausa, pero solo
// una pausa o una liberación invalida: si no, la respuesta de /me que cierra la revalidación publicaba
// un número nuevo y mataba la escritura que acababa de autorizarse contra ella.
let invalidatedAt = 0;
// Autorización ya resuelta contra /me. Vive aparte del estado que publica React: guardada dentro de
// `session` la pisaba la siguiente publicación del provider, que sigue anunciando la revalidación en
// curso aunque la mutación ya tenga su respuesta fresca.
let confirmation = null;

export function getEditorialRequestRevision() {
  return revision;
}

// Publicar no invalida por sí solo: solo lo hace un cambio real de identidad o de permiso. Antes, la
// respuesta de /me que cierra una revalidación publicaba una revisión nueva y mataba en el interceptor
// la escritura que acababa de autorizarse contra ella.
export function publishEditorialRequestSession(owner, access, authToken) {
  revision++;
  const changed =
    !session || session.authToken !== authToken || session.access?.role !== access?.role || session.access?.data?.restricted !== access?.data?.restricted;
  if (changed) {
    invalidatedAt = revision;
    confirmation = null;
  }
  session = { owner, access, authToken, paused: false };
}

export function pauseEditorialRequestSession(owner) {
  if (session?.owner === owner) {
    revision++;
    invalidatedAt = revision;
    confirmation = null;
    session = { ...session, paused: true };
  }
}

export function releaseEditorialRequestSession(owner) {
  if (session?.owner === owner) {
    revision++;
    invalidatedAt = revision;
    confirmation = null;
    session = null;
  }
}

const scopeIdentity = (resources) =>
  JSON.stringify((Array.isArray(resources) ? resources : []).map((resource) => ({ type: resource?.type, guid: resource?.guid, status: resource?.status })));

export function publishEditorialRequestScope(owner, resources) {
  revision++;
  // Un ámbito distinto sí invalida: sin esto una petición del programa anterior se despacharía contra el
  // ámbito del nuevo.
  if (!scope || scopeIdentity(scope.resources) !== scopeIdentity(resources)) {
    invalidatedAt = revision;
    confirmation = null;
  }
  scope = { owner, resources, paused: false };
}

export function pauseEditorialRequestScope(owner) {
  if (scope?.owner === owner) {
    revision++;
    invalidatedAt = revision;
    confirmation = null;
    scope = { ...scope, paused: true };
  }
}

export function releaseEditorialRequestScope(owner) {
  if (scope?.owner === owner) {
    revision++;
    invalidatedAt = revision;
    confirmation = null;
    scope = null;
  }
}

// La autorización de una mutación acaba de traer y comprobar /me: a partir de ahí la sesión ya no está
// a ciegas, así que el transporte deja de retener sus peticiones. No se toca la revisión, de modo que
// lo que naciera antes de la pausa sigue muriendo por desfase.
export function confirmEditorialRequestSession(data) {
  if (!session) return;
  confirmation = {
    authToken: session.authToken,
    role: session.access?.role,
    restricted: data?.restricted,
    scope: confirmation?.scope ?? null,
    at: revision,
  };
  session = { ...session, access: { ...session.access, status: 'ready', data, revalidating: false }, paused: false };
}

// El ámbito se pausa igual que la sesión cuando el boundary revalida el recurso. La autorización acaba
// de comprobar cada programa o contenido afectado, así que también aquí deja de estar a ciegas: sin
// esto la escritura autorizada moría en el interceptor por el ámbito, aunque la sesión ya estuviera al
// día.
export function confirmEditorialRequestScope() {
  if (!scope) return;
  if (confirmation) confirmation = { ...confirmation, scope: scopeIdentity(scope.resources) };
  scope = { ...scope, resources: scope.resources.map((resource) => ({ ...resource, revalidating: false })), paused: false };
}

// Una petición nacida después de la autorización, con la misma sesión y el mismo ámbito, no espera a que
// React termine de anunciar la revalidación: su permiso ya se comprobó contra /me.
function isAuthorizedRequest(config) {
  return (
    !!confirmation &&
    confirmation.authToken === session.authToken &&
    confirmation.role === session.access?.role &&
    confirmation.restricted === session.access?.data?.restricted &&
    confirmation.scope === scopeIdentity(scope?.resources) &&
    config.editorialRevision >= confirmation.at
  );
}

export function canSendEditorialRequest(config, authToken) {
  if (config.editorialAction === undefined) return true;
  if (
    !['read', 'write'].includes(config.editorialAction) ||
    !session ||
    !authToken ||
    session.authToken !== authToken ||
    !Object.values(USER_ROLES).includes(session.access?.role)
  )
    return false;
  if (session.access.role !== USER_ROLES.publisher) return true;
  // La revision solo puede invalidar a quien esta sujeto a la feature: sube en cada publish/pause
  // del arranque y los efectos de React corren de hijo a padre, asi que toda peticion lanzada al
  // montar una pagina nacia desfasada y moria cancelada aunque el rol nunca estuviera restringido.
  // Un R07 ya resuelto como no restringido conserva su acceso mientras se revalida: el resultado de /me
  // cerrará el paso si el perfil cambió, sin descartar entre medias el clic que devolvió el foco.
  if (session.access.status === 'ready' && session.access.data?.restricted === false) return true;
  if (config.editorialRevision < invalidatedAt) return false;
  if (session.access.status !== 'ready' || !session.access.data || typeof session.access.data.restricted !== 'boolean') return false;
  if (!scope || !Array.isArray(scope.resources)) return false;
  const authorized = isAuthorizedRequest(config);
  if (!authorized && (session.paused || session.access.revalidating)) return false;
  if (!authorized && (scope.paused || scope.resources.some((resource) => resource?.revalidating))) return false;
  const capabilities = authorized ? getSettledEditorialCapabilities(session.access, scope.resources) : getEditorialCapabilities(session.access, scope.resources);
  return config.editorialAction === 'read' ? capabilities.canRead : capabilities.canWrite;
}
